import { devices_config } from '@devices/index';
import type {
	ChartDataInterface as DsChartDataInterface,
	ChartStatisticRangePeriod,
} from '@Shelly/shelly-design-system';
import type { AvatarColorVariantTypes } from '@Shelly/shelly-design-system';
import { convertToValueAndUnit } from 'src/helpers/units_helpers';
import { isThreeEM, real_id } from 'src/helpers/interface_helpers';
import type { Device } from 'src/types/Device';
import type { ChartDataInterface } from 'src/types/Chart';
import type { EnergyStatisticsResult } from './fetch_energy_statistics';
import { CONSUMER_MIX_COLORS } from './energy_demo_data';

const MAX_ROOMS_WITH_CHARTS = 8;

export type RoomDeviceRow = {
	id: string;
	seriesKey?: string;
	title: string;
	valueLabel: string;
	totalWh: number;
	color: string;
	excludedFromRoomTotal?: boolean;
};

export type RoomChartModel = {
	id: string;
	name: string;
	icon: string;
	color: AvatarColorVariantTypes;
	totalLabel: string;
	reportedCost: number | null;
	chartData: DsChartDataInterface | null;
	devices: RoomDeviceRow[];
};

export type RoomChartPointTotal = {
	dt: number;
	valueWh: number;
};

const readConsumptionSeries = (chartData: ChartDataInterface | null | undefined) => {
	if (!chartData?.series?.length) return null;
	return (
		chartData.series.find((s) => s.name === 'consumption' && !s.prevPeriod && s.data?.length) ??
		chartData.series.find((s) => !s.prevPeriod && s.data?.length) ??
		null
	);
};

const extractPoints = (chartData: ChartDataInterface | null | undefined) => {
	const series = readConsumptionSeries(chartData);
	if (!series?.data?.length) return [];
	return series.data
		.filter((point) => point.dt != null)
		.map((point) => ({
			dt: point.dt as number,
			value: Math.max(Number(point.value ?? 0), 0),
		}));
};

const sumSeriesWh = (chartData: ChartDataInterface | null | undefined) =>
	extractPoints(chartData).reduce((sum, point) => sum + point.value, 0);

export const sumReportedHistoryCost = (
	history: EnergyStatisticsResult['history'] | null | undefined,
): number | null => {
	const reported = (history ?? []).filter(
		(entry) => !entry?.missing && entry.cost != null && Number.isFinite(Number(entry.cost)),
	);
	return reported.length
		? reported.reduce((sum, entry) => sum + Math.max(Number(entry.cost), 0), 0)
		: null;
};

export const getRoomChartPointTotals = (
	chartData: DsChartDataInterface | null | undefined,
	hiddenSeriesIds: string[] = [],
): RoomChartPointTotal[] => {
	const totals = new Map<number, number>();
	(chartData?.series ?? [])
		.filter((series) => series.visible !== false && !hiddenSeriesIds.includes(series.name))
		.forEach((series) =>
			(series.data ?? []).forEach((point) => {
				const dt = Number(point.dt);
				const value = Number(point.value ?? 0);
				if (!Number.isFinite(dt) || !Number.isFinite(value)) return;
				totals.set(dt, (totals.get(dt) ?? 0) + Math.max(value, 0));
			}),
		);
	return [...totals.entries()].map(([dt, valueWh]) => ({ dt, valueWh })).sort((a, b) => a.dt - b.dt);
};

const resolveSeriesName = (name: string, index: number, usedNames: Set<string>) => {
	const base = name?.trim() || `Device ${index + 1}`;
	if (!usedNames.has(base)) {
		usedNames.add(base);
		return base;
	}

	let suffix = 2;
	while (usedNames.has(`${base} (${suffix})`)) suffix += 1;
	const unique = `${base} (${suffix})`;
	usedNames.add(unique);
	return unique;
};

export const getPmDeviceIdsKey = (roomId: number | string, devices: Record<string, Device>) =>
	getPmDevicesInRoom(roomId, devices)
		.map((device) => device.id)
		.join(',');

export const getPmDevicesInRoom = (roomId: number | string, devices: Record<string, Device>): Device[] =>
	Object.values(devices)
		.filter(
			(device) =>
				device &&
				!device.shared &&
				String(device.room_id) === String(roomId) &&
				devices_config[device.type]?.isPM,
		)
		.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

export const getRoomsWithPmDevices = (
	allRooms: Record<
		string | number,
		{
			id: number | string;
			name: string;
			position?: number;
			icon?: string;
			backgroundColor?: AvatarColorVariantTypes;
		}
	>,
	devices: Record<string, Device>,
) =>
	Object.values(allRooms ?? {})
		.filter((room) => getPmDevicesInRoom(room.id, devices).length > 0)
		.sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
		.slice(0, MAX_ROOMS_WITH_CHARTS);

export type RoomListEntry = {
	id: number | string;
	name: string;
	position?: number;
	icon?: string;
	backgroundColor?: AvatarColorVariantTypes;
};

export const getAllRoomsSorted = (allRooms: Record<string | number, RoomListEntry>): RoomListEntry[] =>
	Object.values(allRooms ?? {}).sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

export const buildRoomDeviceChartData = (
	roomId: string | number,
	deviceEntries: Array<{
		id: string;
		name: string;
		chartData: ChartDataInterface | null;
	}>,
	template: ChartDataInterface | null | undefined,
): DsChartDataInterface | null => {
	const usedNames = new Set<string>();
	const seriesNames = deviceEntries.map((entry, index) =>
		resolveSeriesName(entry.name, index, usedNames),
	);
	const seriesLabels = Object.fromEntries(
		deviceEntries.map((entry, index) => [
			seriesNames[index],
			entry.name?.trim() || seriesNames[index],
		]),
	);

	const series = deviceEntries
		.map((entry, index) => {
			const data = extractPoints(entry.chartData);
			if (!data.length) return null;
			return {
				name: seriesNames[index],
				style: `room_device_${index}`,
				visible: true,
				data,
			};
		})
		.filter(Boolean) as DsChartDataInterface['series'];

	if (!series.length) {
		const fallback = extractPoints(template);
		if (!fallback.length) return null;
		return {
			id: `room-${roomId}`,
			name: `room-${roomId}`,
			type: 'bar',
			units: 'Wh',
			height: 160,
			rangePeriod: template?.rangePeriod as ChartStatisticRangePeriod,
			rangeInterval: template?.rangeInterval as DsChartDataInterface['rangeInterval'],
			timezone: template?.timezone,
			thumbMode: true,
			seriesLabels: { consumption: 'Total' },
			series: [
				{
					name: 'consumption',
					style: 'consumption',
					visible: true,
					data: fallback,
				},
			],
		} as DsChartDataInterface;
	}

	return {
		id: `room-${roomId}`,
		name: `room-${roomId}`,
		type: 'bar',
		units: 'Wh',
		height: 160,
		rangePeriod: template?.rangePeriod as ChartStatisticRangePeriod,
		rangeInterval: template?.rangeInterval as DsChartDataInterface['rangeInterval'],
		timezone: template?.timezone,
		thumbMode: true,
		seriesLabels,
		series,
	} as DsChartDataInterface;
};

export const buildRoomDeviceRows = (
	deviceEntries: Array<{
		device: Device;
		result: EnergyStatisticsResult | null;
	}>,
): RoomDeviceRow[] =>
	deviceEntries
		.map(({ device, result }, index) => {
			const totalWh = result?.statistic?.totalConsumedValue ?? sumSeriesWh(result?.chartData);
			if (totalWh <= 0) return null;
			const formatted = convertToValueAndUnit(totalWh, 'Wh');
			return {
				id: String(device.id),
				title: device.name,
				valueLabel: `${formatted.value} ${formatted.unit}`,
				totalWh,
				color: CONSUMER_MIX_COLORS[index % CONSUMER_MIX_COLORS.length],
				excludedFromRoomTotal: Boolean(device.no_room_cons),
			};
		})
		.filter(Boolean)
		.sort((a, b) => b!.totalWh - a!.totalWh) as RoomDeviceRow[];

type AuthoritativeDeviceStatistic = {
	consumption?: number;
	reversed?: number;
};

export const buildAuthoritativeRoomDeviceRows = (
	statistics: EnergyStatisticsResult['devices'],
	devices: Record<string, Device>,
	roomId?: string | number,
): RoomDeviceRow[] => {
	const rows = new Map<
		string,
		{
			id: string;
			title: string;
			totalWh: number;
			excludedFromRoomTotal: boolean;
		}
	>();
	Object.entries((statistics ?? {}) as Record<string, AuthoritativeDeviceStatistic>).forEach(
		([reportedId, statistic]) => {
			const realId = real_id(reportedId);
			const device = devices[reportedId] ?? devices[realId];
			const consolidate = Boolean(device && isThreeEM(device.type) && device.mode !== 'monophase');
			const id = consolidate ? realId : String(device?.id ?? reportedId);
			let title = device?.name ?? `Deleted Device id: ${reportedId}`;
			if (!consolidate && reportedId.includes('_')) {
				const channel = Number(reportedId.slice(reportedId.lastIndexOf('_') + 1));
				if (Number.isFinite(channel)) title = `${title} (${channel + 1} channel)`;
			}
			const totalWh = Math.max(Number(statistic?.consumption ?? 0), 0);
			const excludedFromRoomTotal = Boolean(
				device?.no_room_cons && (roomId == null || String(device.room_id) === String(roomId)),
			);
			if (totalWh <= 0) return;
			const existing = rows.get(id);
			if (existing) {
				existing.totalWh += totalWh;
				existing.excludedFromRoomTotal ||= excludedFromRoomTotal;
			} else rows.set(id, { id, title, totalWh, excludedFromRoomTotal });
		},
	);

	return [...rows.values()]
		.sort((left, right) => right.totalWh - left.totalWh)
		.map((row, index) => {
			const formatted = convertToValueAndUnit(row.totalWh, 'Wh');
			return {
				...row,
				valueLabel: `${formatted.value} ${formatted.unit}`,
				color: CONSUMER_MIX_COLORS[index % CONSUMER_MIX_COLORS.length],
			};
		});
};

export const buildRoomChartModel = (
	room: {
		id: number | string;
		name: string;
		icon?: string;
		backgroundColor?: AvatarColorVariantTypes;
	},
	roomStats: EnergyStatisticsResult | null,
	deviceEntries: Array<{
		device: Device;
		result: EnergyStatisticsResult | null;
	}>,
	devices: Record<string, Device> = {},
): RoomChartModel => {
	const totalWh = roomStats?.statistic?.totalConsumedValue ?? sumSeriesWh(roomStats?.chartData);
	const totalFormatted = convertToValueAndUnit(totalWh, 'Wh');
	const chartDeviceEntries = deviceEntries.filter(({ device }) => !device.no_room_cons);
	const chartData = buildRoomDeviceChartData(
		room.id,
		chartDeviceEntries.map(({ device, result }) => ({
			id: String(device.id),
			name: device.name,
			chartData: result?.chartData ?? null,
		})),
		roomStats?.chartData,
	);
	const authoritativeRows = roomStats?.devices
		? buildAuthoritativeRoomDeviceRows(roomStats.devices, devices, room.id)
		: buildRoomDeviceRows(deviceEntries);
	const deviceRows = authoritativeRows.map((row) => {
		const sourceIndex = chartDeviceEntries.findIndex(
			({ device }) =>
				String(device.id) === row.id || real_id(String(device.id)) === real_id(row.id),
		);
		const seriesKey = row.excludedFromRoomTotal
			? undefined
			: chartData?.series.find((series) => series.style === `room_device_${sourceIndex}`)?.name;
		return { ...row, seriesKey };
	});

	return {
		id: String(room.id),
		name: room.name,
		icon: room.icon ?? 'fa-light fa-door-open',
		color: (room.backgroundColor ?? 'color-primary') as AvatarColorVariantTypes,
		totalLabel: totalWh > 0 ? `${totalFormatted.value} ${totalFormatted.unit}` : '—',
		reportedCost: sumReportedHistoryCost(roomStats?.history),
		chartData,
		devices: deviceRows,
	};
};
